import {
  getLatestDataSourceInventory,
  getMaskedInventorySummary,
  inventoryContentOf,
  listEncryptJobs,
  listDecryptJobs,
  mergeEncryptionJobsForDataSource,
  readableJobError,
} from 'Secure/api';
import { getCoverageGaugeData } from 'Secure/Utils';

/** Direction of a job: a decrypt job (by jobType) vs encrypt. */
function isDecryptJob(job) {
  return String(job && job.jobType ? job.jobType : '').toUpperCase().includes('DECRYPT');
}

/**
 * Headline stats from the most recent encrypt/decrypt job for the source: a direction-aware
 * status, rows processed, wall-clock duration, and an error count.
 */
function latestJobStats(merged) {
  const job = (merged || [])[0]; // mergeEncryptionJobsForDataSource sorts newest-first
  if (!job) {
    return { statusLabel: 'UNSECURED', rowsProcessed: null, durationMs: null, errorCount: null };
  }
  const decrypt = isDecryptJob(job);
  let statusLabel = 'UNSECURED';
  switch (job.status) {
    case 'PENDING':
    case 'RUNNING':
      statusLabel = 'RUNNING';
      break;
    case 'SUCCEEDED':
      statusLabel = decrypt ? 'DECRYPTED' : 'ENCRYPTED';
      break;
    case 'FAILED':
      statusLabel = 'ERROR';
      break;
    case 'CANCELLED':
      statusLabel = 'CANCELLED';
      break;
    default:
      statusLabel = 'UNSECURED';
  }
  const startMs = Date.parse(job.startedAt);
  const finishMs = Date.parse(job.finishedAt);
  const durationMs =
    Number.isFinite(startMs) && Number.isFinite(finishMs) && finishMs >= startMs ? finishMs - startMs : null;
  return {
    statusLabel,
    rowsProcessed: job.recordsProcessed != null ? Number(job.recordsProcessed) : null,
    durationMs,
    errorCount: job.status === 'FAILED' ? 1 : 0,
  };
}

/**
 * All data the Encryption Summary page needs — the encryption analogue of getMaskingSummary.
 * "What is FPE-encrypted" comes from the saved inventory; the operation history merges encrypt +
 * decrypt jobs (color-coded by the page) so a source's secure/unsecure timeline is visible.
 */
const getEncryptionSummary = async (dataSourceId) => {
  const numericId = Number(dataSourceId);
  if (!Number.isFinite(numericId)) {
    throw new Error(`Invalid data source id "${dataSourceId}".`);
  }

  // FPE column coverage from the saved inventory (most recently updated one under the
  // multi-inventory model).
  let inventory = null;
  try {
    inventory = inventoryContentOf(await getLatestDataSourceInventory(numericId));
  } catch (e) {
    inventory = null;
  }
  const invSummary = getMaskedInventorySummary(inventory);

  // Encrypt + decrypt jobs for this source.
  let encryptJobs = [];
  let decryptJobs = [];
  try {
    const [encRes, decRes] = await Promise.all([
      listEncryptJobs({ dataSourceId: numericId, page: 0, size: 500, sort: ['id,desc'] }),
      listDecryptJobs({ dataSourceId: numericId, page: 0, size: 500, sort: ['id,desc'] }),
    ]);
    encryptJobs = encRes && Array.isArray(encRes.content) ? encRes.content : [];
    decryptJobs = decRes && Array.isArray(decRes.content) ? decRes.content : [];
  } catch (e) {
    /* leave empty — page renders an empty history */
  }

  const merged = mergeEncryptionJobsForDataSource(encryptJobs, decryptJobs, numericId);
  const stats = latestJobStats(merged);

  // Color-coded operation history rows (encrypt vs decrypt). transformEncryptionJobsToReportRows
  // produces parent (per-source) + child rows; for a single source we flatten to a simple list
  // newest-first with operation + status + a readable error.
  const historyRows = (merged || []).map((job) => ({
    jobId: job.id,
    operation: isDecryptJob(job) ? 'Decryption' : 'Encryption',
    date: job.finishedAt || job.startedAt || null,
    status: job.status,
    errorMessage: job.status === 'FAILED' ? readableJobError(job) : null,
  }));

  const coverage = getCoverageGaugeData(invSummary.maskedFields, invSummary.totalFields);

  return {
    ...invSummary,
    statusLabel: stats.statusLabel,
    coveragePercent: coverage.percent,
    rowsProcessed: stats.rowsProcessed,
    durationMs: stats.durationMs,
    errorCount: stats.errorCount,
    historyRows,
  };
};

export { getEncryptionSummary };
